'use client';

import { type FC, useEffect, useState } from 'react';
import { SkinImage } from './ui/SkinImage';
import { cn } from '../utils/cn';
import { useLocalTranslation } from '../hooks/useLocalTranslation';
import translations from './AuctionRibbonCard.translations';
import { Credits } from './Credits';
import { rarityGradient } from '../utils/rarityGradient';
import type { ActiveAuction } from '../services/generated/Api';
import { FaClock } from './icons';

interface AuctionRibbonCardProps {
  auction: ActiveAuction;
  onClick?: () => void;
}

function pad(n: number): string {
  return n.toString().padStart(2, '0');
}

export const AuctionRibbonCard: FC<AuctionRibbonCardProps> = ({ auction, onClick }) => {
  const { t } = useLocalTranslation(translations);
  const [timeRemaining, setTimeRemaining] = useState<string>('');

  const isPending = auction.status === 'PENDING';
  const isActive = auction.status === 'ACTIVE';

  useEffect(() => {
    const tick = () => {
      const now = Date.now();
      const target = isPending && auction.startTime
        ? new Date(auction.startTime).getTime()
        : auction.endTime
          ? new Date(auction.endTime).getTime()
          : null;

      if (target === null) {
        setTimeRemaining(t('noTime'));
        return;
      }
      const remaining = Math.max(0, target - now);
      if (remaining === 0) {
        setTimeRemaining(isPending ? t('starting') : t('ended'));
        return;
      }
      const h = Math.floor(remaining / 3_600_000);
      const m = Math.floor((remaining % 3_600_000) / 60_000);
      const s = Math.floor((remaining % 60_000) / 1000);
      setTimeRemaining(`${pad(h)}:${pad(m)}:${pad(s)}`);
    };

    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [auction.endTime, auction.startTime, isPending, t]);

  const handleClick = () => {
    if (isPending) return;
    onClick?.();
  };

  const badge = isActive
    ? { label: t('statusLive'), cls: 'bg-accent-green/15 text-accent-green' }
    : isPending
      ? { label: t('statusSoon'), cls: 'bg-accent-blue/15 text-accent-blue' }
      : { label: t('statusEnded'), cls: 'bg-fill-tertiary text-label-tertiary' };

  const timerText =
    isPending && timeRemaining.includes(':') ? `${t('startsIn')} ${timeRemaining}` : timeRemaining;

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={isPending}
      className={cn(
        'relative flex w-full items-center gap-3 overflow-hidden rounded-2xl bg-fill-tertiary p-2.5 text-left transition',
        isPending ? 'cursor-default' : 'active:opacity-80',
      )}
    >
      {/* Item art over the shared rarity gradient (same as item lists everywhere). */}
      <div
        className="relative h-24 w-24 shrink-0 overflow-hidden rounded-xl"
        style={{ background: rarityGradient(auction.item.rarityColor) }}
      >
        <span
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-25"
          style={{ background: 'radial-gradient(circle at 22% 12%, rgba(255,255,255,0.55), transparent 55%)' }}
        />
        <SkinImage
          name={auction.item.name}
          src={auction.item.imageUrl}
          className="absolute inset-0"
          imgClassName="h-full w-full object-contain"
          imgStyle={{ filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.5))' }}
        />
      </div>

      <div className="min-w-0 flex-1">
        <div className="truncate pr-14 text-sm font-semibold text-label-primary">{auction.item.name}</div>

        <div className="mt-1 flex flex-wrap items-center gap-x-1.5 text-xs text-label-secondary">
          <span>{isPending ? t('startPrice') : t('currentBid')}</span>
          <Credits amount={auction.currentBid} iconSize={13} className="text-sm font-bold text-label-primary" />
          {auction.bidCount > 0 && (
            <span className="text-label-tertiary">· {auction.bidCount} {t('bidsShort')}</span>
          )}
        </div>

        <div className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-black/25 px-2.5 py-1 text-xs font-semibold tabular-nums text-label-secondary">
          <FaClock className="text-[11px] text-label-tertiary" />
          {timerText}
        </div>
      </div>

      <span className={cn('absolute right-3 top-3 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide', badge.cls)}>
        {badge.label}
      </span>
    </button>
  );
};
